import { defineStore } from "pinia";
import type { Pagination } from "@/types.ts";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { StoreModule } from "@/constants/store.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import { CustomDomainDnsAction } from "@/constants/network.ts";

export interface BaseCustomDomainDnsItem {
  ip: string;
  domain: string;
}

export interface CustomDomainDnsItem extends BaseCustomDomainDnsItem {
  id: string;
  createdAt: number;
}

export interface CustomDomainDnsState {
  total: number;
  items: Array<CustomDomainDnsItem>;
  loading: boolean;
  showSkeletonLoader: boolean;
  pagination: Pagination;
}

const defaultCustomDomainDnsState: CustomDomainDnsState = {
  total: 0,
  items: [],
  loading: false,
  showSkeletonLoader: false,
  pagination: {
    page: 0,
    pageSize: 25,
  },
};

export const useCustomDomainDnsStore = defineStore(StoreModule.CUSTOM_DOMAIN_DNS, {
  state: (): CustomDomainDnsState => ({ ...defaultCustomDomainDnsState }),
  actions: {
    async getItems() {
      const filters = useFiltersStore().filters[FilterContext.CUSTOM_DOMAIN_DNS];
      const requestConfig = {
        ...api.customDomainDns(),
        method: "GET",
        params: {
          ...this.pagination,
          ...filters,
        },
      };

      try {
        this.loading = true;
        const {
          data: { items, total },
        } = await axiosInstance.request(requestConfig);
        this.items = items;
        this.total = total;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async deleteItems(data: string[]) {
      const requestConfig = {
        ...api.customDomainDns(),
        method: "DELETE",
        data,
      };

      try {
        await axiosInstance.request(requestConfig);
        await this.getItems();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.customDomainDns.${CustomDomainDnsAction.REMOVE}`
          )
        );
      } catch (e) {
        console.error(e);
      }
    },
    async createItem(data: BaseCustomDomainDnsItem) {
      const requestConfig = {
        ...api.customDomainDns(),
        method: "POST",
        data,
      };

      try {
        await axiosInstance.request(requestConfig);
        await this.getItems();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.customDomainDns.${CustomDomainDnsAction.ADD}`
          )
        );
      } catch (e) {
        console.error(e);
      }
    },
  },
});
